import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as apiPublic from "@api/public";
import Card from "@components/card";
import Icon from "@components/icon";
import facebookIcon from "@assets/facebook.svg";
import instagramIcon from "@assets/instagram.svg";
import { useNavigator } from "@contexts/NavigatorContext";
import { ACCOUNT_TYPE, checkConstant } from "@logic/constants";
import { getBusinessLogoUrl } from "@logic/user";
import { getSponsorImageUrl } from "@logic/sponsor";
import annuncioStyles from "@scenes/annuncio/Annuncio.module.css";
import styles from "./SponsorDestinazione.module.css";

const SponsorDestinazione = () => {
  const { currentRoute, changePageTitle } = useNavigator();
  const [businessUser, setBusinessUser] = useState(null);

  const sponsorId = useMemo(() => currentRoute?.params?.[0], [currentRoute?.params]);
  const userId = useMemo(() => currentRoute?.params?.[1], [currentRoute?.params]);

  const destinationImage = useMemo(() => (
    getSponsorImageUrl({ userId, sponsorId, imageName: "destination" })
  ), [sponsorId, userId]);

  const isBusinessOwner = checkConstant(ACCOUNT_TYPE.AZIENDA, businessUser?.accountType);

  const displayEmail = useMemo(() => (
    businessUser?.displayEmail || businessUser?.displayMail || ""
  ), [businessUser?.displayEmail, businessUser?.displayMail]);

  const getLinkWithProtocol = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    return `https://${value}`;
  }, []);

  useEffect(() => {
    if (!userId) {
      return;
    }

    apiPublic.GetUser({ userId })
      .then((data) => {
        setBusinessUser(data);
        if (data?.advertisementName) {
          changePageTitle(data.advertisementName);
        }
      })
      .catch(() => {
        setBusinessUser(null);
      });
  }, [changePageTitle, userId]);

  const phones = [businessUser?.tel, businessUser?.cel, businessUser?.cel2].filter(Boolean);
  const phonesText = phones.length > 0 ? `Tel. ${phones.join(" - ")}` : "";

  const businessLogoUrl = useMemo(() => (
    getBusinessLogoUrl({ userId: businessUser?.id })
  ), [businessUser?.id]);

  if (!sponsorId || !userId) {
    return (
      <div className="row">
        <div className="col">
          <span className="page-title">Sponsor non trovato</span>
          <p>Verifica che il link sia corretto o che lo sponsor sia ancora disponibile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col">
        <div className={styles.wrapper}>
          {destinationImage ? (
            <div className={styles.imageCard}>
              <img src={destinationImage} alt="Sponsor" className={styles.hero} />
            </div>
          ) : (
            <div className={styles.imageCard}>
              <span className={styles.placeholder}>Immagine sponsor non disponibile</span>
            </div>
          )}
          {isBusinessOwner && (
            <Card>
              <div className="row">
                <div className="col">
                  <span className="page-section">Informazioni sull'azienda</span>
                </div>
              </div>
              <div className={annuncioStyles.businessInfoWrapper}>
                <div
                  className={annuncioStyles.businessImagePlaceholder}
                  aria-hidden={businessLogoUrl ? undefined : "true"}
                >
                  {businessLogoUrl ? (
                    <img
                      src={businessLogoUrl}
                      alt={`Logo ${businessUser?.advertisementName || "azienda"}`}
                      className={annuncioStyles.businessImage}
                    />
                  ) : (
                    <Icon
                      name="storefront"
                      size={48}
                      fill={1}
                      weight={400}
                    />
                  )}
                </div>
                <div className={annuncioStyles.businessInfoTextBlock}>
                  <div className={annuncioStyles.businessRowHeader}>
                    {businessUser?.advertisementName}
                  </div>
                  <div className={annuncioStyles.businessRow}>
                    {businessUser?.address}
                  </div>
                  <div className={annuncioStyles.businessRow}>
                    {phonesText}
                  </div>
                  <div className={annuncioStyles.businessRow}>
                    {displayEmail && (
                      <a
                        href={`mailto:${displayEmail}`}
                        className={annuncioStyles.businessContactLink}
                      >
                        {displayEmail}
                      </a>
                    )}
                  </div>
                  <div className={annuncioStyles.businessRow}>
                    <a
                      href={getLinkWithProtocol(businessUser?.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={annuncioStyles.businessSocialLink}
                    >
                      {businessUser?.website}
                    </a>
                  </div>
                  <div className={annuncioStyles.businessRow}>
                    {businessUser?.facebook && (
                      <a
                        href={getLinkWithProtocol(businessUser?.facebook)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={annuncioStyles.businessSocialLink}
                      >
                        <img src={facebookIcon} alt="Facebook" className={annuncioStyles.businessSocialIcon} />
                      </a>
                    )}
                    {businessUser?.instagram && (
                      <a
                        href={getLinkWithProtocol(businessUser?.instagram)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={annuncioStyles.businessSocialLink}
                      >
                        <img src={instagramIcon} alt="Instagram" className={annuncioStyles.businessSocialIcon} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SponsorDestinazione;
